<script lang="ts">
  export let isAuthenticated: boolean = false;
  export let responseMessage: string = "";
  export let loading: boolean = false;
  export let showRefreshInput: boolean = false;
  export let refreshTokenValue: string = "";

  export let onLogin: () => void = () => {};
  export let onLogout: () => void = () => {};
  export let onToggleRefreshInput: () => void = () => {};
  export let onRefreshTokenInput: (value: string) => void = () => {};
  export let onSubmitRefreshToken: () => void = () => {};

  const handleRefreshTokenInput = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    onRefreshTokenInput(target.value);
  };

  $: panelTitle = isAuthenticated ? "HubSpot is connected" : "Connect HubSpot to sync opportunities";
  $: panelDescription = isAuthenticated
    ? "You are ready to sync opportunities."
    : "Sign in to HubSpot to start syncing opportunities.";
  $: actionLabel = isAuthenticated ? "Connection" : "Sign in";
  $: actionDescription = isAuthenticated
    ? "Your HubSpot connection is active."
    : "Choose how you want to connect your HubSpot account.";
</script>

<section class="rounded-2xl border border-white/10 bg-[#24283a] p-5 text-white">
  <div class="auth-panel-layout">
    <div class="auth-panel-intro">
      <p class="auth-panel-eyebrow">
        HubSpot Sync
      </p>
      <h2 class="auth-panel-title">
        {panelTitle}
      </h2>
      <p class="auth-panel-copy">
        {panelDescription}
      </p>
    </div>

    <div class="auth-panel-actions-card">
      <div class="auth-panel-actions-head">
        <p class="auth-panel-actions-eyebrow">{actionLabel}</p>
        <p class="auth-panel-actions-copy">{actionDescription}</p>
      </div>

      {#if isAuthenticated}
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="text-sm font-semibold text-emerald-300">Connected</p>
            <p class="mt-1 text-sm text-white/65">
              Opportunity Sync can use your current HubSpot session.
            </p>
          </div>
          <button
            type="button"
            class="auth-panel-logout-button"
            on:click={onLogout}
            disabled={loading}
          >
            {loading ? "Working..." : "Logout"}
          </button>
        </div>
      {:else}
        <div class="flex flex-col gap-3">
          <button
            type="button"
            class="auth-panel-primary-button"
            on:click={onLogin}
            disabled={loading}
          >
            {loading ? "Opening HubSpot..." : "Connect with HubSpot"}
          </button>

          <button
            type="button"
            class="auth-panel-secondary-button"
            on:click={onToggleRefreshInput}
          >
            {showRefreshInput ? "Hide refresh token option" : "Use refresh token"}
          </button>
        </div>
      {/if}

      {#if showRefreshInput && !isAuthenticated}
        <div class="auth-panel-token-box">
          <label class="block text-sm font-medium text-white/80" for="hubspot-refresh-token-local">
            Refresh token
          </label>
          <input
            id="hubspot-refresh-token-local"
            type="text"
            class="auth-panel-token-input"
            value={refreshTokenValue}
            on:input={handleRefreshTokenInput}
            placeholder="Paste a HubSpot refresh token"
          />
          <button
            type="button"
            class="auth-panel-token-button"
            on:click={onSubmitRefreshToken}
            disabled={loading || !refreshTokenValue.trim()}
          >
            {loading ? "Connecting..." : "Connect with token"}
          </button>
        </div>
      {/if}

      {#if responseMessage}
        <div class="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          {responseMessage}
        </div>
      {/if}
    </div>
  </div>
</section>

<style>
  .auth-panel-eyebrow {
    margin: 0;
    color: rgba(251, 146, 60, 0.92);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    line-height: 1.2;
    text-transform: uppercase;
  }

  .auth-panel-title {
    margin: 0.5rem 0 0;
    color: rgba(255, 255, 255, 0.96);
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.12;
  }

  .auth-panel-copy {
    margin: 0.8rem 0 0;
    max-width: 30rem;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.98rem;
    line-height: 1.55;
  }

  .auth-panel-layout {
    display: grid;
    gap: 1.25rem;
  }

  .auth-panel-intro {
    min-width: 0;
  }

  .auth-panel-actions-card {
    width: 100%;
    min-width: 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1.25rem;
    background: rgba(17, 17, 28, 0.22);
    padding: 1.15rem;
  }

  .auth-panel-actions-head {
    margin-bottom: 0.9rem;
  }

  .auth-panel-actions-eyebrow {
    margin: 0;
    color: rgba(255, 255, 255, 0.56);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    line-height: 1.2;
    text-transform: uppercase;
  }

  .auth-panel-actions-copy {
    margin: 0.45rem 0 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.94rem;
    line-height: 1.45;
  }

  .auth-panel-primary-button,
  .auth-panel-secondary-button,
  .auth-panel-token-button,
  .auth-panel-logout-button {
    appearance: none;
    border-radius: 0.95rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.2;
    min-height: 3rem;
    padding: 0.82rem 1rem;
    text-align: center;
    transition:
      background-color 150ms ease,
      border-color 150ms ease,
      color 150ms ease,
      box-shadow 150ms ease,
      transform 150ms ease,
      opacity 150ms ease;
    width: 100%;
  }

  .auth-panel-primary-button {
    background: linear-gradient(180deg, #ff8b2a 0%, #ff7414 100%);
    border: 1px solid rgba(255, 179, 102, 0.42);
    box-shadow:
      0 10px 24px rgba(255, 116, 20, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.18);
    color: #fff7ed;
  }

  .auth-panel-primary-button:hover:enabled {
    background: linear-gradient(180deg, #ff973e 0%, #ff7f20 100%);
    box-shadow:
      0 12px 28px rgba(255, 116, 20, 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  .auth-panel-secondary-button,
  .auth-panel-token-button,
  .auth-panel-logout-button {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
  }

  .auth-panel-secondary-button:hover:enabled,
  .auth-panel-token-button:hover:enabled,
  .auth-panel-logout-button:hover:enabled {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .auth-panel-token-box {
    margin-top: 0.9rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    background: rgba(42, 42, 62, 0.8);
    padding: 0.95rem;
  }

  .auth-panel-token-input {
    width: 100%;
    margin-top: 0.6rem;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0.95rem;
    background: rgba(0, 0, 0, 0.18);
    color: rgba(255, 255, 255, 0.96);
    font-size: 0.95rem;
    line-height: 1.2;
    outline: none;
    padding: 0.85rem 0.95rem;
    transition:
      border-color 150ms ease,
      box-shadow 150ms ease,
      background-color 150ms ease;
  }

  .auth-panel-token-input::placeholder {
    color: rgba(255, 255, 255, 0.42);
  }

  .auth-panel-token-input:focus {
    border-color: rgba(255, 163, 79, 0.7);
    box-shadow: 0 0 0 3px rgba(255, 145, 44, 0.14);
    background: rgba(0, 0, 0, 0.26);
  }

  .auth-panel-token-button {
    margin-top: 0.8rem;
  }

  .auth-panel-primary-button:disabled,
  .auth-panel-secondary-button:disabled,
  .auth-panel-token-button:disabled,
  .auth-panel-logout-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }

  @media (min-width: 1024px) {
    .auth-panel-layout {
      align-items: stretch;
      grid-template-columns: minmax(0, 1.15fr) minmax(24rem, 1fr);
    }

    .auth-panel-actions-card {
      height: 100%;
    }
  }

  @media (max-width: 1023px) {
    .auth-panel-title {
      font-size: 1.45rem;
    }
  }
</style>
